import { createFileRoute } from "@tanstack/react-router";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { listCategories, listPublishedArticles } from "@/lib/blog.functions";
import { BlogSplitView } from "@/components/blog/BlogSplitView";
import { Reveal } from "@/components/Reveal";
import { BookOpen } from "lucide-react";

const SITE = "https://heallexa.com";
const BLOG_URL = `${SITE}/blog`;
const TITLE = "Heallexa Blog: Healthcare Discovery Guides";
const DESCRIPTION =
  "Patient and provider guides on finding healthcare providers by specialty, location, insurance, and condition.";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: BLOG_URL },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: BLOG_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: TITLE,
          description: DESCRIPTION,
          url: BLOG_URL,
          inLanguage: "en-US",
          publisher: {
            "@type": "Organization",
            name: "Heallexa",
            url: SITE,
            logo: { "@type": "ImageObject", url: `${SITE}/logo.png` },
          },
          about: [
            { "@type": "Thing", name: "Healthcare Provider Discovery" },
            { "@type": "Thing", name: "Medical Specialties" },
            { "@type": "Thing", name: "Health Insurance" },
          ],
          isPartOf: { "@id": `${SITE}/#website` },
        }),
      },
    ],
  }),
  component: BlogIndex,
});

const PAGE_SIZE = 24;

function BlogIndex() {
  const { data: cats } = useQuery({ queryKey: ["blog-cats"], queryFn: () => listCategories() });
  const { data: pages, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["blog-latest-paged"],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => listPublishedArticles({ data: { limit: PAGE_SIZE, offset: pageParam as number } }),
    getNextPageParam: (last, all) => (last.hasMore ? all.length * PAGE_SIZE : undefined),
  });

  const { catMap, catSlugMap } = useMemo(() => {
    const m = new Map<string, string>();
    const sm = new Map<string, string>();
    (cats ?? []).forEach((c: any) => {
      m.set(c.id, c.name);
      sm.set(c.id, c.slug);
    });
    return { catMap: m, catSlugMap: sm };
  }, [cats]);

  const rows = useMemo(() => {
    const all = (pages?.pages ?? []).flatMap((p: any) => p.rows ?? []);
    return all.map((a: any) => ({
      ...a,
      category_name: catMap.get(a.category_id) ?? null,
      category_slug: catSlugMap.get(a.category_id) ?? null,
    }));
  }, [pages, catMap, catSlugMap]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-10 px-6 border-b border-border bg-gradient-to-b from-muted/40 to-background">
        <div className="mx-auto max-w-[1400px]">
          <Reveal from="up">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-primary font-semibold">
              <BookOpen className="h-3.5 w-3.5" />
              Heallexa Blog
            </div>
            <h1 className="mt-3 text-[clamp(1.75rem,3.6vw,2.75rem)] font-medium tracking-[-0.035em] leading-[1.1] max-w-[900px] text-foreground">
              Healthcare discovery,<br /><span className="text-primary">made simple</span>
            </h1>
            <p className="mt-3 text-muted-foreground max-w-[720px] text-[15px] leading-relaxed">
              Find the right provider, confirm insurance, understand specialties, and learn how to book care. Informational only, not medical advice.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Master-Detail Split View Section */}
      <section className="mx-auto max-w-[1400px] px-6 py-8">
        <BlogSplitView
          categories={cats ?? []}
          articles={rows}
          initialCategorySlug="all"
          onLoadMore={() => fetchNextPage()}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
        />
      </section>

      <FooterSection />
    </div>
  );
}


